/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationUsersListComponent
 * -----------------------------
 * Renders a full, theme-aware listing UI for application-level users,
 * including search, pagination, action controls, and responsive layout.
 *
 * Features:
 * • Server-driven search with controlled input + submit button
 * • Reusable paginator with ellipsis and compact navigation behavior
 * • Sticky “Actions” column for consistent UX on wide tables
 * • Fully responsive header where “Add User” button auto-stacks below 640px
 * • Clean card-based layout matching the application's UI theme
 *
 * Props:
 * - users: Array of user records to display
 * - page: Current page number
 * - pageCount: Total number of pages
 * - totalCount: Total number of users returned by the backend
 * - search: Current search text
 * - setSearch: Setter to trigger server-side filtering
 * - onCreate: Handler → Add User
 * - onEdit: Handler → Edit selected user
 * - onDelete: Handler → Delete selected user
 * - onPage: Handler for pagination navigation
 * - onAssignRole: Handler → Assign/Change role for user
 * - onTablePermissions: Handler → Open table permissions screen
 * - onView: Handler → View user details (opens modal)
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import Button from '../Button';
import Paginator from '../common/Paginator';
import ThemedInput from '../common/ThemedInput';

function ApplicationUsersListComponent({
    appName,
    users = [],
    page = 1,
    pageCount = 1,
    totalCount = 0,
    search = '',
    setSearch = () => { },
    onCreate,
    onEdit,
    onDelete,
    onPage,
    onAssignRole,
    onAssignGroups,
    onTablePermissions,
    onView,
}) {
    const { theme } = useTheme();

    const [forceCreateDown, setForceCreateDown] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

    useEffect(() => {
        function handleResize() {
            setForceCreateDown(window.innerWidth < 640);
            setIsMobile(window.innerWidth <= 991);
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [searchInput, setSearchInput] = useState(search || '');
    useEffect(() => setSearchInput(search || ''), [search]);

    const handleSearchChange = (e) => setSearchInput(e.target.value);

    const hasSearch = !!search && search.trim().length > 0;
    const inputMatchesSearch = (searchInput || '').trim() === (search || '').trim();
    const showClear = hasSearch && inputMatchesSearch;

    const handleSearchClick = () => {
        if (showClear) {
            setSearchInput('');
            setSearch('');
        } else {
            const term = (searchInput || '').trim();
            setSearch(term);
        }
    };

    const triggerSearch = () => {
        const term = (searchInput || '').trim();
        setSearch(term);
    };

    // Handlers
    function handleCreateLocal() {
        if (onCreate) onCreate();
    }
    function handleEditLocal(user) {
        if (onEdit) onEdit(user);
    }
    function handleAssignRoleLocal(user) {
        if (onAssignRole) onAssignRole(user);
    }
    function handleAssignGroupsLocal(user) {
        if (onAssignGroups) onAssignGroups(user);
    }
    function handleTablePermissionsLocal(user) {
        if (onTablePermissions) onTablePermissions(user);
    }
    function handleDeleteLocal(user) {
        if (onDelete) onDelete(user);
    }
    function handleViewLocal(user) {
        if (onView) onView(user);
    }

    const cardStyle = {
        width: isMobile ? '100%' : '100%',
        maxWidth: '100%',
        background: '#fff',
        borderRadius: isMobile ? 0 : theme.cardRadius,
        boxShadow: '0 6px 32px 0 rgba(80,80,180,0.10)',
        padding: isMobile ? '1rem' : '2em',
        margin: isMobile ? '0' : '32px auto',
        position: 'relative'
    };
    const actionsColStyle = {
        position: 'sticky',
        right: 0,
        background: '#fff',
        zIndex: 2,
        boxShadow: '0 0 0 1px #eee'
    };

    return (
        <div
            style={{
                minHeight: '80vh',
                background: '#f6f8fa',
                paddingTop: 20,
                paddingBottom: 40,
                paddingLeft: isMobile ? 0 : 20,
                paddingRight: isMobile ? 0 : 20
            }}
        >
            <div style={cardStyle}>
                <h2 className="fw-bold mb-4" style={{ color: theme.primary }}>
                    Application Users {appName ? `- ${appName}` : ""}
                </h2>

                {/* Search + Add User */}
                <div className="mb-3">
                    <div
                        className={`d-flex ${forceCreateDown ? 'flex-column' : 'flex-row'} align-items-stretch`}
                        style={{ gap: forceCreateDown ? 0 : 26 }}
                    >
                        <div className={forceCreateDown ? "w-100 mb-2" : "flex-grow-1"}>
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <div style={{ display: 'flex', width: '100%', gap: 8 }}>
                                    <ThemedInput
                                        value={searchInput}
                                        onChange={handleSearchChange}
                                        onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
                                        placeholder="Search User"
                                        style={{ flex: 1 }}
                                        aria-label="Search users"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSearchClick}
                                        aria-label={showClear ? "Clear search" : "Search"}
                                        disabled={!showClear && inputMatchesSearch}
                                        style={{
                                            backgroundColor: showClear ? '#dc3545' : theme.primary,
                                            borderRadius: theme.buttonRadius,
                                            padding: "8px 12px",
                                            cursor: 'pointer',
                                            border: "none",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            minWidth: 40,
                                        }}
                                    >
                                        <i
                                            className={`bi ${showClear ? 'bi-x-lg' : 'bi-search'}`}
                                            style={{ color: "white" }}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={forceCreateDown ? "w-100" : ""}>
                            <Button
                                mode="primary-bold"
                                onClick={handleCreateLocal}
                                style={{
                                    width: forceCreateDown ? "100%" : "auto",
                                    marginTop: forceCreateDown ? 13 : 0
                                }}
                            >
                                + Add User
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Desktop View */}
                <div className="table-responsive d-none d-lg-block" style={{ overflowX: "auto" }}>
                    <table
                        style={{
                            width: "100%",
                            minWidth: "740px",
                            borderCollapse: "collapse",
                            marginBottom: 0
                        }}
                    >
                        <thead>
                            <tr
                                style={{
                                    background: theme.primary + '08',
                                    color: theme.primary,
                                    fontWeight: 600,
                                    fontSize: 16
                                }}
                            >
                                <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>Username</th>
                                <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>Email Address</th>
                                <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>First Name</th>
                                <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>Last Name</th>
                                <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>Role</th>
                                <th
                                    style={{
                                        ...actionsColStyle,
                                        minWidth: 140,
                                        textAlign: 'center',
                                        borderBottom: `2px solid ${theme.primary}15`
                                    }}
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!users || users.length === 0) ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        style={{
                                            textAlign: "center",
                                            padding: "22px",
                                            color: "#bbb"
                                        }}
                                    >
                                        No users found.
                                    </td>
                                </tr>
                            ) : users.map(user => (
                                <tr key={user.id} style={{ fontSize: 15 }}>
                                    <td style={{ padding: "14px 10px", fontWeight: 500 }}>{user.username}</td>
                                    <td style={{ padding: "14px 10px", color: "#666" }}>{user.email}</td>
                                    <td style={{ padding: "14px 10px" }}>
                                        {user.first_name ?? user.firstName ?? ''}
                                    </td>
                                    <td style={{ padding: "14px 10px" }}>
                                        {user.last_name ?? user.lastName ?? ''}
                                    </td>
                                    <td style={{ padding: '14px 10px', color: theme.primary }}>
                                        {user.role?.name || ''}
                                    </td>
                                    <td
                                        style={{
                                            ...actionsColStyle,
                                            padding: "14px 8px",
                                            textAlign: "center",
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {/* View */}
                                        <span
                                            style={{ marginRight: 9, cursor: "pointer", color: theme.primary }}
                                            title="View details"
                                            onClick={() => handleViewLocal(user)}
                                        >
                                            <i className="bi bi-eye" />
                                        </span>
                                        {/* Edit */}
                                        <span
                                            style={{ marginRight: 9, cursor: "pointer", color: theme.primary }}
                                            title="Edit"
                                            onClick={() => handleEditLocal(user)}
                                        >
                                            <i className="bi bi-pencil-square" />
                                        </span>
                                        {/* Assign Role */}
                                        <span
                                            style={{ marginRight: 9, cursor: "pointer", color: theme.primary }}
                                            title="Assign Role"
                                            onClick={() => handleAssignRoleLocal(user)}
                                        >
                                            <i className="bi bi-person-gear" />
                                        </span>
                                        {/* Assign Groups */}
                                        <span
                                            style={{ marginRight: 9, cursor: "pointer", color: theme.primary }}
                                            title="Assign Groups"
                                            onClick={() => handleAssignGroupsLocal(user)}
                                        >
                                            <i className="bi bi-diagram-3" />
                                        </span>
                                        {/* Table Permissions */}
                                        <span
                                            style={{ marginRight: 9, cursor: "pointer", color: theme.primary }}
                                            title="Table Permissions"
                                            onClick={() => handleTablePermissionsLocal(user)}
                                        >
                                            <i className="bi bi-person-lock" />
                                        </span>
                                        {/* Delete */}
                                        <span
                                            style={{ cursor: "pointer", color: "#d53b3b" }}
                                            title="Delete"
                                            onClick={() => handleDeleteLocal(user)}
                                        >
                                            <i className="bi bi-trash" />
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="d-lg-none">
                    {(!users || users.length === 0) ? (
                        <div className="text-center p-4 text-muted">No users found.</div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {users.map(user => (
                                <div
                                    key={user.id}
                                    className="p-3 bg-white rounded border shadow-sm"
                                    style={{ borderColor: theme.primary }}
                                >
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div
                                            className="fw-bold fs-5 text-break"
                                            style={{ color: theme.primary }}
                                        >
                                            {user.username}
                                        </div>
                                        <span className="badge bg-light text-primary border">
                                            {user.role?.name || 'No Role'}
                                        </span>
                                    </div>

                                    <div className="mb-2">
                                        <div
                                            className="small text-muted text-uppercase fw-bold"
                                            style={{ fontSize: '11px' }}
                                        >
                                            Email
                                        </div>
                                        <div className="text-break">{user.email}</div>
                                    </div>

                                    <div className="d-flex flex-column gap-2 mb-3">
                                        <div>
                                            <div
                                                className="small text-muted text-uppercase fw-bold"
                                                style={{ fontSize: '11px' }}
                                            >
                                                First Name
                                            </div>
                                            <div>{user.first_name || user.firstName || '-'}</div>
                                        </div>
                                        <div>
                                            <div
                                                className="small text-muted text-uppercase fw-bold"
                                                style={{ fontSize: '11px' }}
                                            >
                                                Last Name
                                            </div>
                                            <div>{user.last_name || user.lastName || '-'}</div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top">
                                        {/* View */}
                                        <button
                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                                            onClick={() => handleViewLocal(user)}
                                        >
                                            <i className="bi bi-eye"></i> View
                                        </button>
                                        {/* Edit */}
                                        <button
                                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                            onClick={() => handleEditLocal(user)}
                                        >
                                            <i className="bi bi-pencil-square"></i> Edit
                                        </button>
                                        {/* Role */}
                                        <button
                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                                            onClick={() => handleAssignRoleLocal(user)}
                                        >
                                            <i className="bi bi-person-gear"></i> Role
                                        </button>
                                        {/* Groups */}
                                        <button
                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                                            onClick={() => handleAssignGroupsLocal(user)}
                                        >
                                            <i className="bi bi-diagram-3"></i> Groups
                                        </button>
                                        {/* Permissions */}
                                        <button
                                            className="btn btn-sm btn-outline-info d-flex align-items-center gap-2"
                                            onClick={() => handleTablePermissionsLocal(user)}
                                        >
                                            <i className="bi bi-person-lock"></i> Perms
                                        </button>
                                        {/* Delete */}
                                        <button
                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-2"
                                            onClick={() => handleDeleteLocal(user)}
                                        >
                                            <i className="bi bi-trash"></i> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Paginator
                    page={page}
                    pageCount={pageCount}
                    onPrevious={onPage}
                    onPage={onPage}
                    onNext={onPage}
                    maxPagesToShow={3}
                />

                <div
                    style={{
                        height: 8,
                        width: '100%',
                        borderBottomLeftRadius: theme.cardRadius,
                        borderBottomRightRadius: theme.cardRadius,
                        background: theme.gradientBackground,
                        position: 'absolute',
                        left: 0,
                        bottom: 0
                    }}
                />
            </div>
        </div>
    );
}

export default ApplicationUsersListComponent;
